/**
 * MODO ESTRITO DE CORREDOR (módulo puro — sem IA, sem Maps).
 *
 * Genérico por TIPO de experiência: qualquer corredor curado (Nacional 2,
 * Route 66, NC500, Romantische Straße, Great Ocean Road…) passa a ter a
 * mesma garantia dura:
 *
 *  1. nenhuma paragem fora do traçado oficial entra no dia;
 *  2. os pontos de navegação seguem sempre a ordem do corredor (nunca recuam
 *     nem saltam para outra via);
 *  3. o cálculo no Google Maps é sempre feito com "evitar autoestradas"
 *     quando o corredor declara vias que o descaracterizam.
 */

import {
  deviationFromCorridorKm,
  type CuratedCorridor,
  type CorridorWaypoint,
} from "../catalog/corridor-routes";

export type StrictStop = {
  kind?: string;
  category?: string;
  name: string;
  time?: string;
  lat?: number | null;
  lng?: number | null;
  templateId?: string | null;
  [key: string]: unknown;
};

const fold = (value: string) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLowerCase()
    .trim();

/** O corredor exige evitar autoestradas/atalhos que o descaracterizam. */
export function corridorRequiresAvoidHighways(
  corridor: CuratedCorridor | null | undefined,
): boolean {
  return !!corridor && corridor.disallowedShortcuts.length > 0;
}

function corridorOrderIndex(corridor: CuratedCorridor, stop: StrictStop): number {
  const key = fold(stop.name.replace(/^[^\p{L}\p{N}]+/u, ""));
  const named = corridor.waypoints.findIndex((waypoint) => {
    const candidate = fold(waypoint.name);
    return candidate === key || key.includes(candidate) || candidate.includes(key);
  });
  if (named >= 0) return named;
  if (typeof stop.lat === "number" && typeof stop.lng === "number") {
    let best = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    corridor.waypoints.forEach((waypoint, index) => {
      const distance =
        (waypoint.lat - stop.lat!) ** 2 + (waypoint.lng - stop.lng!) ** 2;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }
  return -1;
}

/** A paragem está dentro da tolerância oficial do corredor? */
export function isStopOnCorridor(
  corridor: CuratedCorridor,
  stop: StrictStop,
  maxKm = corridor.maxDeviationKm,
): boolean {
  if (typeof stop.lat !== "number" || typeof stop.lng !== "number") return true;
  return deviationFromCorridorKm(corridor, { lat: stop.lat, lng: stop.lng }) <= maxKm;
}

/**
 * Pontos que o Maps deve seguir num dia de corredor: apenas paragens do
 * traçado, sem repetições e sempre por ordem de progressão.
 */
export function strictCorridorNavigationStops<S extends StrictStop>(
  corridor: CuratedCorridor,
  stops: S[],
): S[] {
  const onCorridor = stops.filter(
    (stop) =>
      typeof stop.lat === "number" &&
      typeof stop.lng === "number" &&
      isStopOnCorridor(corridor, stop),
  );
  const withIndex = onCorridor.map((stop, position) => ({
    stop,
    position,
    index: corridorOrderIndex(corridor, stop),
  }));
  const forward = [...withIndex].sort((a, b) =>
    a.index === b.index ? a.position - b.position : a.index - b.index,
  );
  const reverse = [...forward].reverse();
  // A direção real da etapa é dada pela ordem em que o motor produziu as
  // paragens: se o dia avança para trás no corredor (percurso invertido),
  // mantemos essa direção em vez de forçar o sentido dos dados.
  const first = withIndex[0]?.index ?? 0;
  const last = withIndex.at(-1)?.index ?? 0;
  const ordered = last >= first ? forward : reverse;

  const seen = new Set<string>();
  const unique: S[] = [];
  for (const entry of ordered) {
    const key = `${entry.stop.lat!.toFixed(4)},${entry.stop.lng!.toFixed(4)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(entry.stop);
  }
  return unique;
}

/**
 * Remove das etapas qualquer paragem que obrigue a sair do traçado e
 * reordena os pontos de estrada pela progressão oficial do corredor.
 */
export function enforceStrictCorridorDays<
  S extends StrictStop,
  D extends { stops: S[] },
>(days: D[], corridor: CuratedCorridor | null | undefined): D[] {
  if (!corridor) return days;
  return days.map((day) => {
    if (day.stops.some((stop) => stop.category === "return")) return day;
    const kept = day.stops.filter(
      (stop) => stop.category === "scenic_road" || isStopOnCorridor(corridor, stop),
    );
    const road = kept.filter((stop) => stop.category === "scenic_road");
    if (road.length < 2) return { ...day, stops: kept };
    const orderedRoad = strictCorridorNavigationStops(corridor, road);
    const queue = [...orderedRoad];
    const stops = kept.flatMap((stop) => {
      if (stop.category !== "scenic_road") return [stop];
      const next = queue.shift();
      return next ? [{ ...next, time: stop.time } as S] : [];
    });
    return { ...day, stops };
  });
}

/** Pontos oficiais do corredor entre dois extremos de etapa (inclusive). */
export function corridorSegmentWaypoints(
  corridor: CuratedCorridor,
  from: string | undefined,
  to: string,
): CorridorWaypoint[] {
  const names = corridor.waypoints.map((waypoint) => fold(waypoint.name));
  const fromIndex = from ? names.findIndex((name) => name.includes(fold(from))) : -1;
  const toIndex = names.findIndex((name) => name.includes(fold(to)));
  if (toIndex < 0) return [];
  const start = fromIndex >= 0 ? Math.min(fromIndex, toIndex) : toIndex;
  const end = Math.max(fromIndex, toIndex);
  return corridor.waypoints.slice(start, end + 1);
}
